/**
 * Map Editor Constants
 * Elvis Aloha Collection - Arena Map Editor
 * 
 * Shared constants for overlay categories, tools, and publication status.
 * 
 * IMPORTANT:
 * - Toggle options are NOT hard-coded here - they come from the API
 * - Categories are for geometry organization, NOT classification
 * - Each chart has independent crop/viewport settings
 */

// Tool definitions
export const TOOLS = {
  SELECT: 'select',
  HAND: 'hand', // Permanent pan/hand tool
  DRAW_RECT: 'draw_rect',
  DRAW_POLYGON: 'draw_polygon',
  DRAW_SEAT: 'draw_seat',  // Click-drag manual seat box (local draft)
  PLACE_SEAT: 'place_seat', // Template-based single-click seat (local draft)
  PLACE_MARKER: 'place_marker'
};

// Tool configurations for UI
// Note: DRAW_SEAT creates arbitrary seat boxes (legacy)
// PLACE_SEAT uses the seat template system (preferred)
export const TOOL_CONFIG = [
  { tool: TOOLS.SELECT, icon: '↖', label: 'Select', forcesCategory: null },
  { tool: TOOLS.DRAW_RECT, icon: '▢', label: 'Rectangle', forcesCategory: null },
  { tool: TOOLS.DRAW_POLYGON, icon: '⬡', label: 'Polygon', forcesCategory: null },
  { tool: TOOLS.PLACE_SEAT, icon: '⬚', label: 'Place Seat', forcesCategory: 'seat_area', useTemplate: true },
  { tool: TOOLS.PLACE_MARKER, icon: '●', label: 'Marker', forcesCategory: null }
];

// Geometry categories (NOT classifications - those are managed separately)
// These are broad geometry organization types, not ticket classifications
export const OVERLAY_CATEGORIES = [
  { id: 'section', label: 'Section', color: '#4A90D9' },
  { id: 'seat_area', label: 'Seat Area', color: '#C9A962' },
  { id: 'stage', label: 'Stage/Production', color: '#8B7355' },
  { id: 'camera', label: 'Camera Position', color: '#4682B4' },
  { id: 'reference', label: 'Reference Zone', color: '#607D8B' },
  { id: 'custom', label: 'Custom Zone', color: '#9C27B0' }
];

// Empty toggle option - actual toggles come from GET /api/seat-map/public-toggles
export const EMPTY_TOGGLE_OPTION = {
  value: '',
  label: '-- None --'
};

// Publication workflow statuses
export const PUBLICATION_STATUS = {
  DRAFT: 'draft',
  OWNER_REVIEW: 'owner_review',
  APPROVED: 'approved',
  PUBLISHED: 'published',
  UNPUBLISHED: 'unpublished',
  ARCHIVED: 'archived',
  REQUIRES_REVISION: 'requires_revision'
};

// Status colors for UI
export const STATUS_COLORS = {
  [PUBLICATION_STATUS.DRAFT]: '#888888',
  [PUBLICATION_STATUS.OWNER_REVIEW]: '#F5A623',
  [PUBLICATION_STATUS.APPROVED]: '#4A90D9',
  [PUBLICATION_STATUS.PUBLISHED]: '#4CAF50',
  [PUBLICATION_STATUS.UNPUBLISHED]: '#9E9E9E',
  [PUBLICATION_STATUS.ARCHIVED]: '#795548',
  [PUBLICATION_STATUS.REQUIRES_REVISION]: '#E91E63'
};

/**
 * Valid status transitions
 * 
 * IMPORTANT: unpublished → published is ONLY valid when:
 * - has_unreviewed_changes === false
 * - OR current_revision_id === approved_revision_id
 * 
 * The frontend must check these conditions before enabling the transition.
 * The backend MUST enforce this rule and reject invalid transitions.
 */
export const VALID_TRANSITIONS = {
  [PUBLICATION_STATUS.DRAFT]: [
    PUBLICATION_STATUS.OWNER_REVIEW,
    PUBLICATION_STATUS.ARCHIVED
  ],
  [PUBLICATION_STATUS.OWNER_REVIEW]: [
    PUBLICATION_STATUS.DRAFT,
    PUBLICATION_STATUS.APPROVED,
    PUBLICATION_STATUS.REQUIRES_REVISION,
    PUBLICATION_STATUS.ARCHIVED
  ],
  [PUBLICATION_STATUS.APPROVED]: [
    PUBLICATION_STATUS.OWNER_REVIEW,
    PUBLICATION_STATUS.PUBLISHED,
    PUBLICATION_STATUS.ARCHIVED
  ],
  [PUBLICATION_STATUS.PUBLISHED]: [
    PUBLICATION_STATUS.UNPUBLISHED,
    PUBLICATION_STATUS.REQUIRES_REVISION
  ],
  // unpublished → published requires revision check (see note above)
  [PUBLICATION_STATUS.UNPUBLISHED]: [
    PUBLICATION_STATUS.OWNER_REVIEW,
    PUBLICATION_STATUS.PUBLISHED, // Conditional - see canRepublishWithoutReview()
    PUBLICATION_STATUS.ARCHIVED
  ],
  [PUBLICATION_STATUS.ARCHIVED]: [
    PUBLICATION_STATUS.DRAFT // Restore
  ],
  [PUBLICATION_STATUS.REQUIRES_REVISION]: [
    PUBLICATION_STATUS.DRAFT,
    PUBLICATION_STATUS.OWNER_REVIEW,
    PUBLICATION_STATUS.ARCHIVED
  ]
};

// Chart definitions with INDEPENDENT crop bounds per chart
export const CHART_IDS = {
  CONCERT_STAGE: 'concert-stage-reference',
  STAGE_360: '360-stage-reference',
  BOXING: 'boxing-reference'
};

export const CHARTS = {
  [CHART_IDS.CONCERT_STAGE]: {
    id: CHART_IDS.CONCERT_STAGE,
    label: 'Concert Stage',
    url: '/assets/charts/arena-stage-layout-1.png',
    width: 2550,
    height: 3300,
    // Desktop arena-only crop: frames the circular seating bowl (wider aspect)
    arenaCrop: { x: 280, y: 620, width: 1990, height: 1200 },
    // Mobile-specific crop: excludes title, focuses on seating bowl + stage
    // y=600 starts below title, ends above legend
    // Narrower width to reduce side letterboxing on portrait screens
    mobileCrop: { x: 500, y: 600, width: 1550, height: 1450 },
    // Full chart bounds (includes title header and legend)
    fullChartBounds: { x: 0, y: 0, width: 2550, height: 3300 },
    panConstraint: null
  },
  [CHART_IDS.STAGE_360]: {
    id: CHART_IDS.STAGE_360,
    label: '360 Stage',
    url: '/assets/charts/arena-360-stage-1.png',
    width: 2550,
    height: 3300,
    arenaCrop: { x: 280, y: 620, width: 1990, height: 1200 },
    mobileCrop: { x: 500, y: 600, width: 1550, height: 1450 },
    fullChartBounds: { x: 0, y: 0, width: 2550, height: 3300 },
    panConstraint: null
  },
  [CHART_IDS.BOXING]: {
    id: CHART_IDS.BOXING,
    label: 'Boxing',
    url: '/assets/charts/arena-boxing-1.png',
    width: 2550,
    height: 3300,
    arenaCrop: { x: 280, y: 620, width: 1990, height: 1200 },
    mobileCrop: { x: 500, y: 600, width: 1550, height: 1450 },
    fullChartBounds: { x: 0, y: 0, width: 2550, height: 3300 },
    panConstraint: null
  }
};

// Resize handle positions
export const RESIZE_HANDLES = {
  NW: 'nw', NE: 'ne', SW: 'sw', SE: 'se',
  N: 'n', S: 's', E: 'e', W: 'w'
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get category color by ID
 */
export const getCategoryColor = (categoryId) => {
  return OVERLAY_CATEGORIES.find(c => c.id === categoryId)?.color || '#888';
};

/**
 * Get human-readable status label
 */
export const getStatusLabel = (status) => {
  return (status || 'draft').replace(/_/g, ' ');
};

/**
 * Get valid next statuses for a given current status
 * Note: Does not account for revision checks - use canRepublishWithoutReview() for that
 */
export const getValidNextStatuses = (currentStatus) => {
  return VALID_TRANSITIONS[currentStatus] || [];
};

/**
 * Check if a basic status transition is in the allowed list
 * Note: For unpublished → published, also call canRepublishWithoutReview()
 */
export const isValidTransition = (fromStatus, toStatus) => {
  const validTargets = VALID_TRANSITIONS[fromStatus] || [];
  return validTargets.includes(toStatus);
};

/**
 * Check if an unpublished overlay can be republished without going through review
 * 
 * Returns true only if:
 * - has_unreviewed_changes is false/undefined
 * - OR current_revision_id matches approved_revision_id
 */
export const canRepublishWithoutReview = (overlay) => {
  if (!overlay) return false;
  if (overlay.publication_status !== PUBLICATION_STATUS.UNPUBLISHED) return false;
  
  // If no revision tracking fields, assume needs review (safe default)
  if (overlay.has_unreviewed_changes === true) return false;
  
  // If revision IDs exist and don't match, needs review
  if (overlay.current_revision_id && overlay.approved_revision_id) {
    return overlay.current_revision_id === overlay.approved_revision_id;
  }
  
  // If no changes tracked and no revision mismatch, allow republish
  return overlay.has_unreviewed_changes !== true;
};

/**
 * Generate a professional temporary name for a new overlay
 */
export const generateTempName = (type, existingOverlays = []) => {
  const typeLabels = {
    'rect': 'Rectangle',
    'polygon': 'Polygon',
    'marker': 'Marker',
    'seat_box': 'Seat Box'
  };
  
  const label = typeLabels[type] || 'Shape';
  
  // Find highest existing number for this type
  const pattern = new RegExp(`^Draft ${label} (\\d+)$`);
  let maxNum = 0;
  
  existingOverlays.forEach(o => {
    const match = o.name?.match(pattern);
    if (match) {
      maxNum = Math.max(maxNum, parseInt(match[1], 10));
    }
  });
  
  return `Draft ${label} ${String(maxNum + 1).padStart(3, '0')}`;
};

/**
 * Get the forced category for a tool, if any
 */
export const getToolForcedCategory = (tool) => {
  const config = TOOL_CONFIG.find(t => t.tool === tool);
  return config?.forcesCategory || null;
};
